import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createEngine, type Engine, type ProjectConfig } from "@super-gantt/engine";
import { applyOp, type PlanOp, type PlanState } from "@super-gantt/core";
import { api, CLIENT_ID, type ProjectDetail } from "./api";

export interface LiveProject {
  loading: boolean;
  error: string | null;
  project: ProjectDetail | null;
  config: ProjectConfig | null;
  engine: Engine | null;
  state: PlanState | null;
  revision: number;
  /** True while local ops are waiting for the server. */
  saving: boolean;
  /** Who else changed the board most recently (from live updates). */
  lastRemote: { actorName: string; at: number } | null;
  /**
   * Applies ops locally at once and sends them to the server. Returns false (and leaves the
   * board unchanged) when an op cannot apply; the reason goes to onError.
   */
  dispatch: (ops: PlanOp | PlanOp[]) => boolean;
  reload: () => Promise<void>;
}

/**
 * Loads a project, keeps it live over server-sent events, and applies changes optimistically.
 * The server is authoritative: any gap or rejection triggers a full reload.
 */
export function useProject(id: string, onError?: (msg: string) => void): LiveProject {
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [state, setState] = useState<PlanState | null>(null);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const [lastRemote, setLastRemote] = useState<LiveProject["lastRemote"]>(null);
  const stateRef = useRef<PlanState | null>(null);
  const revRef = useRef(0);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const errRef = useRef(onError);
  errRef.current = onError;

  const engine = useMemo(() => (project ? createEngine(project.config) : null), [project?.config]);
  const engineRef = useRef<Engine | null>(null);
  engineRef.current = engine;

  const reload = useCallback(async () => {
    try {
      const p = await api.getProject(id);
      setProject(p);
      stateRef.current = p.state; revRef.current = p.revision;
      setState(p.state); setRevision(p.revision); setError(null);
    } catch (e: any) {
      setError(e.message || String(e));
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const es = new EventSource(`/api/projects/${id}/events`, { withCredentials: true });
    es.addEventListener("ops", (ev: MessageEvent) => {
      const msg = JSON.parse(ev.data) as { revision: number; ops: PlanOp[]; actorName: string; clientId?: string };
      if (msg.clientId === CLIENT_ID) return; // our own write; the POST response advances the revision
      const eng = engineRef.current;
      if (!eng || !stateRef.current || msg.revision !== revRef.current + 1) { reload(); return; }
      try {
        let s = stateRef.current;
        for (const op of msg.ops) s = applyOp(eng, s, op);
        stateRef.current = s; revRef.current = msg.revision;
        setState(s); setRevision(msg.revision);
        setLastRemote({ actorName: msg.actorName, at: Date.now() });
      } catch { reload(); }
    });
    es.addEventListener("config", () => { reload(); });
    es.onerror = () => { /* EventSource reconnects on its own */ };
    return () => es.close();
  }, [id, reload]);

  const dispatch = useCallback((input: PlanOp | PlanOp[]) => {
    const ops = Array.isArray(input) ? input : [input];
    const eng = engineRef.current;
    if (!eng || !stateRef.current) return false;
    let s = stateRef.current;
    try {
      for (const op of ops) s = applyOp(eng, s, op);
    } catch (e: any) {
      errRef.current?.(e.message || String(e));
      return false;
    }
    stateRef.current = s;
    setState(s);
    setPending(n => n + 1);
    // serialize sends so baseRevision always reflects our own previous op
    queue.current = queue.current.then(async () => {
      try {
        const base = revRef.current;
        const res = await api.applyOps(id, base, ops);
        if (res.revision !== base + 1) await reload(); // someone else wrote in between
        else { revRef.current = res.revision; setRevision(res.revision); }
      } catch (e: any) {
        errRef.current?.(`Not saved: ${e.message || e}`);
        await reload();
      } finally {
        setPending(n => n - 1);
      }
    });
    return true;
  }, [id, reload]);

  return {
    loading: !project && !error,
    error,
    project,
    config: project?.config ?? null,
    engine,
    state,
    revision,
    saving: pending > 0,
    lastRemote,
    dispatch,
    reload,
  };
}
